import { useEffect } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { LoadingState, EmptyState, Button } from '../../components/ui';
import { useEditorStore } from '../../store/editorStore';
import styles from './EditorLayout.module.css';

function SaveStatus() {
  const saving = useEditorStore((s) => s.saving);
  const dirty = useEditorStore((s) => s.dirty);
  const error = useEditorStore((s) => s.error);
  if (error) return <span className={styles.statusError}>⚠ {error}</span>;
  if (saving) return <span className={styles.status}>Salvataggio…</span>;
  if (dirty) return <span className={styles.status}>Modifiche non salvate</span>;
  return <span className={styles.statusOk}>✓ Salvato</span>;
}

/** Editor frame: loads the adventure into the editor store, shows a section sidebar + outlet. */
export function EditorLayout() {
  const { slug = '' } = useParams();
  const adventure = useEditorStore((s) => s.adventure);
  const loading = useEditorStore((s) => s.loading);
  const load = useEditorStore((s) => s.load);
  const reset = useEditorStore((s) => s.reset);
  const saveNow = useEditorStore((s) => s.saveNow);

  useEffect(() => {
    void load(slug);
    return () => {
      void saveNow().finally(reset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading && !adventure) return <LoadingState />;
  if (!adventure)
    return (
      <EmptyState
        icon="⚠️"
        title="Avventura non trovata"
        actions={
          <Link to="/library">
            <Button variant="primary">Torna alla libreria</Button>
          </Link>
        }
      />
    );

  const sections = [...adventure.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const adventureSections = sections.filter((s) => (s.scope ?? 'adventure') === 'adventure');
  const systemSections = sections.filter((s) => (s.scope ?? 'adventure') === 'system');

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [styles.navItem, isActive ? styles.navActive : undefined].filter(Boolean).join(' ');

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <Link to={`/library/${slug}`} className={styles.back}>
          ← {adventure.title}
        </Link>
        <NavLink to={`/library/${slug}/edit`} end className={navLinkClass}>
          ⚙️ Sezioni e dati
        </NavLink>

        <div className={styles.groupLabel}>Avventura</div>
        {adventureSections.map((s) => (
          <NavLink key={s.id} to={`/library/${slug}/edit/${s.id}`} className={navLinkClass}>
            <span aria-hidden>{s.icon}</span> {s.title}
            {s.enabled === false && <span className={styles.off}>off</span>}
          </NavLink>
        ))}

        <div className={styles.groupLabel}>Sistema</div>
        {systemSections.map((s) => (
          <NavLink key={s.id} to={`/library/${slug}/edit/${s.id}`} className={navLinkClass}>
            <span aria-hidden>{s.icon}</span> {s.title}
          </NavLink>
        ))}
      </aside>

      <div className={styles.content}>
        <header className={styles.header}>
          <strong>Editor avventura</strong>
          <SaveStatus />
        </header>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
